// src/modules/portal/pages/SearchResultsPage.tsx
// Página de resultados de busca

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { searchTenants } from '../services/portalService';
import { TenantListingCard, SearchFilter, TenantType, CategoryTag } from '../../../core/types/portal';
import { TenantCard } from '../components/TenantCard';
import { SEO } from '../../../components/SEO';

export const SearchResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState<TenantListingCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  
  // Estados dos filtros
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [selectedState, setSelectedState] = useState(searchParams.get('estado') || '');
  const [selectedTypes, setSelectedTypes] = useState<TenantType[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<CategoryTag[]>([]);
  const [minRating, setMinRating] = useState<number>(0);
  const [sortBy, setSortBy] = useState<SearchFilter['sortBy']>('relevance');

  useEffect(() => {
    performSearch();
  }, [searchParams]);

  const performSearch = async () => {
    setLoading(true);
    
    const filters: SearchFilter = {
      query: searchParams.get('q') || undefined,
      state: searchParams.get('estado') || undefined,
      tenantTypes: selectedTypes.length > 0 ? selectedTypes : undefined,
      categoryTags: selectedCategories.length > 0 ? selectedCategories : undefined,
      minRating: minRating > 0 ? minRating : undefined,
      sortBy: sortBy,
    };
    
    const tenants = await searchTenants(filters);
    setResults(tenants);
    setLoading(false);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedState) params.set('estado', selectedState);
    setSearchParams(params);
  };

  const handleTypeToggle = (type: TenantType) => {
    setSelectedTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleCategoryToggle = (category: CategoryTag) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedCategories([]);
    setMinRating(0);
    setSortBy('relevance');
    performSearch();
  };

  const activeFiltersCount = selectedTypes.length + selectedCategories.length + (minRating > 0 ? 1 : 0);

  return (
    <div className="min-h-screen bg-brand-midnight flex flex-col">
      <SEO 
        title={query ? `Busca por "${query}"` : "Explorar Destinos"}
        description={`Encontre as melhores opções de pesca ${query ? `para "${query}"` : ""} em todo o Brasil. Confira avaliações, comodidades e agende sua reserva.`}
      />
      
      {/* Search context summary (Simplified) */}
      <div className="bg-white/5 border-b border-white/10 sticky top-0 z-20 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between">
            <h2 className="text-xl font-black text-white flex items-center gap-3">
              <span className="text-brand-gold italic">Descoberta:</span>
              {query ? `"${query}"` : "Todos os Rios"}
            </h2>
            <div className="text-xs font-black text-white/30 uppercase tracking-[0.3em]">
               {results.length} resultados encontrados
            </div>
        </div>
      </div>
      
      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Filtros Sidebar */}
          <div className={`lg:col-span-1 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <div className="bg-brand-charcoal/30 rounded-2xl p-6 border border-white/10 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-black text-xs uppercase tracking-[0.3em] text-white/30 flex items-center gap-2">
                  <SlidersHorizontal size={14} className="text-brand-gold" />
                  Filtros
                </h3>
                {activeFiltersCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-brand-orange hover:text-orange-700 flex items-center gap-1 font-medium hover:underline"
                  >
                    <X size={16} />
                    Limpar
                  </button>
                )}
              </div>
              
              {/* Tipo de Empresa */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Tipo de Empresa</h4>
                <div className="space-y-2">
                  {['pousada', 'hotel', 'barco-hotel', 'guia', 'agencia', 'loja', 'associacao', 'pesqueiro'].map(type => (
                    <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:text-brand-orange transition-colors">
                      <input 
                        type="checkbox" 
                        className="rounded text-brand-gold focus:ring-brand-gold border-white/20 bg-white/5"
                        checked={selectedTypes.includes(type as TenantType)}
                        onChange={() => handleTypeToggle(type as TenantType)}
                      />
                      <span className="capitalize text-white/60 font-medium">{type.replace('-', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Categorias */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Tipo de Pesca</h4>
                <div className="space-y-2">
                  {['pesca-embarcada', 'pesca-de-barranco', 'fly-fishing', 'all-inclusive'].map(cat => (
                    <label key={cat} className="flex items-center gap-2 text-sm cursor-pointer hover:text-brand-orange transition-colors">
                      <input 
                        type="checkbox" 
                        className="rounded text-brand-gold focus:ring-brand-gold border-white/20 bg-white/5"
                        checked={selectedCategories.includes(cat as CategoryTag)}
                        onChange={() => handleCategoryToggle(cat as CategoryTag)}
                      />
                      <span className="capitalize text-white/60 font-medium">{cat.replace(/-/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Avaliação */}
              <div className="mb-6">
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Avaliação Mínima</h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-white font-medium text-sm focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all"
                  value={minRating}
                  onChange={(e) => setMinRating(Number(e.target.value))}
                  aria-label="Filtrar por avaliação mínima"
                >
                  <option value={0} className="bg-brand-midnight">Todas</option>
                  <option value={3} className="bg-brand-midnight">3+ estrelas</option>
                  <option value={4} className="bg-brand-midnight">4+ estrelas</option>
                  <option value={4.5} className="bg-brand-midnight">4.5+ estrelas</option>
                  <option value={5} className="bg-brand-midnight">5 estrelas</option>
                </select>
              </div>
              
              {/* Ordenação */}
              <div>
                <h4 className="font-bold text-xs text-white/50 uppercase tracking-widest mb-3">Ordenar por</h4>
                <select 
                  className="w-full border border-white/10 bg-white/5 rounded-xl px-3 py-2.5 text-white font-medium text-sm focus:ring-2 focus:ring-brand-gold/30 outline-none transition-all"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SearchFilter['sortBy'])}
                  aria-label="Ordenar resultados"
                >
                  <option value="relevance" className="bg-brand-midnight">Relevância</option>
                  <option value="rating" className="bg-brand-midnight">Melhor Avaliação</option>
                  <option value="recent" className="bg-brand-midnight">Mais Recentes</option>
                </select>
              </div>
              
              <button
                onClick={performSearch}
                className="w-full mt-6 bg-brand-orange text-white font-black px-8 py-4 rounded-xl hover:shadow-glow-orange transition-all font-display uppercase tracking-widest text-xs"
              >
                Aplicar Filtros
              </button>
            </div>
          </div>
          
          {/* Resultados */}
          <div className="lg:col-span-3 flex-grow">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold mb-4"></div>
                <p className="text-white/60 font-medium">Buscando empresas...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="bg-white/5 rounded-3xl p-12 text-center border border-white/10 shadow-xl">
                <div className="text-6xl mb-4 grayscale opacity-50">🔍</div>
                <h3 className="text-2xl font-black text-white mb-2 font-display">
                  Nenhum resultado encontrado
                </h3>
                <p className="text-white/40 mb-6 font-medium">
                  Tente ajustar os filtros ou buscar por outros termos.
                </p>
                <button
                  onClick={clearFilters}
                  className="text-brand-gold hover:underline font-bold"
                >
                  Limpar Filtros
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {results.map(tenant => (
                  <TenantCard key={tenant.id} tenant={tenant} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
